import {
  BI_BASE_API_URL,
  BI_POST_AUTHENTICATION_BY_SERIAL_NUMBER_ENDPOINT,
  BI_GET_AUTHENTICATION_BY_TOKEN_ENDPOINT,
  BI_AUTH_NECESSARY_ROLES,
} from '../constants/authConstants';
import { storeUser } from '../redux/actions/authAction';
import store from '../redux/store';
import httpService from './httpService';
import setAuthHeader from './axiosService';
import { storeUserLanguage } from '../redux/actions/globalizationAction';
import globalizationService from './globalizationService';

const authService = () => {
  const user = {
    authTime: null, // Momento da autenticação
    expiresIn: null, // Tempo até o token expirar
  };

  let isAuthorizedUser = false;

  const postAuthenticationBySerialNumber = async ({ serialNumber, clientCallerURI }) => {
    const { post } = httpService();
    const params = {
      SerialNumber: serialNumber,
      ClientCallerURI: clientCallerURI,
    };

    const result = await post({
      url: BI_BASE_API_URL + BI_POST_AUTHENTICATION_BY_SERIAL_NUMBER_ENDPOINT,
      data: params,
      hideErrorToaster: true,
    });

    return result;
  };

  const getAuthenticationByToken = async () => {
    const { get } = httpService();
    const params = null;

    const result = await get({
      url: BI_BASE_API_URL + BI_GET_AUTHENTICATION_BY_TOKEN_ENDPOINT,
      data: params,
      hideErrorToaster: true,
    });

    return result;
  };

  const getURLQueryStringValues = () => {
    const queryStringValuesObj = {};
    const keyValuePairs = window.location.search !== null && window.location.search !== undefined ? window.location.search.slice(1).split('&') : null;

    if (keyValuePairs !== null) {
      keyValuePairs.forEach((keyValuePair) => {
        const transformedKeyName = keyValuePair.split('=')[0];
        const transformedKeyValue = keyValuePair.split(/=(.+)/)[1];
        queryStringValuesObj[decodeURIComponent(transformedKeyName || '')] = decodeURIComponent(transformedKeyValue) || '';
      });
    }

    return queryStringValuesObj;
  };

  const parseJwt = (token) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''));

    return JSON.parse(jsonPayload);
  };

  const checkNecessaryRoles = (userRoles) => {
    const necessaryRolesArr = BI_AUTH_NECESSARY_ROLES;
    let hasNecessaryHole = false;

    if (Array.isArray(userRoles)) {
      hasNecessaryHole = userRoles.some(option => necessaryRolesArr.includes(option));
    } else {
      hasNecessaryHole = necessaryRolesArr.includes(userRoles);
    }

    if (hasNecessaryHole) {
      isAuthorizedUser = true;
      return isAuthorizedUser;
    }

    return isAuthorizedUser;
  };

  const signinBySerialNumber = async (receivedQueryStringObj, currentUserLanguage) => {
    // Recebe via querystring um numero serial (Guid)
    const { serialNumber } = receivedQueryStringObj;
    let { ref } = receivedQueryStringObj;

    if (ref === null || ref === undefined) {
      const referrer = btoa((window.location !== window.parent.location)
        ? document.referrer.split('?')[0]
        : document.location.href.split('?')[0]);
      ref = referrer;
    }

    // Identifica se usuário está sem autorização por falta do serial number na URL como querystring
    if (serialNumber !== null && serialNumber !== undefined && ref !== null && ref !== undefined) {
      const params = {
        serialNumber,
        clientCallerURI: ref,
      };

      const responseJSON = await postAuthenticationBySerialNumber(params);

      const authenticationResponse = responseJSON !== null && responseJSON !== undefined
      && responseJSON.data !== null && responseJSON.data !== undefined
        ? responseJSON.data.response : null;

      const authenticationStatusCode = authenticationResponse !== null
      && authenticationResponse !== undefined
        ? responseJSON.status : 401;

      // Verifica se o serial number é de usuário autorizado ou não
      if (authenticationResponse !== null
        && authenticationStatusCode === 200
        && authenticationResponse.access_token !== undefined
        && authenticationResponse.access_token !== null) {
        // Salva token no header para cada requisição
        setAuthHeader(authenticationResponse.access_token);

        // Decodifica token jwt
        const parsedAuthenticationResponse = parseJwt(authenticationResponse.access_token);
        const { role } = parsedAuthenticationResponse;
        const authTime = parsedAuthenticationResponse.auth_time;

        user.authTime = authTime;
        user.expiresIn = authenticationResponse.expires_in;

        // Verifica se o usuário tem a role necessária e token
        const hasNecessaryHole = checkNecessaryRoles(role);

        if (hasNecessaryHole && authenticationResponse.access_token) {
          isAuthorizedUser = true;
        }

        // Faz requisição das traduções do Globalization
        await globalizationService()
          .getDataTermAndTranslationsByTag(currentUserLanguage, null, null);

        setTimeout(() => {
          window.history.pushState({}, document.title, '/');
          store.dispatch(storeUserLanguage(currentUserLanguage));
          store.dispatch(storeUser(user, isAuthorizedUser));
        }, 300);

        return user;
      }
    }

    window.history.pushState({}, document.title, '/');

    return store.dispatch(storeUser(user, isAuthorizedUser));
  };

  const signinByToken = async (receivedQueryStringObj, currentUserLanguage) => {
    // Recebe via querystring um token
    const { token } = receivedQueryStringObj;

    // Salva token no header para cada requisição
    setAuthHeader(token);

    // Chama API passando o token para validação
    if (token !== null && token !== undefined) {
      const params = null;
      const responseJSON = await getAuthenticationByToken(params);

      const authenticationResponse = responseJSON !== null && responseJSON !== undefined
      && responseJSON.data !== null && responseJSON.data !== undefined
        ? responseJSON : null;

      const authenticationStatusCode = authenticationResponse !== null
      && authenticationResponse !== undefined
        ? responseJSON.status : 401;

      // Verifica se o token é de usuário autorizado ou não
      if (authenticationStatusCode === 200) {
        // Decodifica token jwt
        const parsedAuthenticationResponse = parseJwt(token);
        const { role, exp } = parsedAuthenticationResponse;
        const authTime = parsedAuthenticationResponse.auth_time;

        user.authTime = authTime;
        user.expiresIn = exp - authTime;

        // Verifica se o usuário tem a role necessária e token
        const hasNecessaryHole = checkNecessaryRoles(role);

        if (hasNecessaryHole && token) {
          isAuthorizedUser = true;
        }

        // Faz requisição das traduções do Globalization
        await globalizationService()
          .getDataTermAndTranslationsByTag(currentUserLanguage, null, null);

        setTimeout(() => {
          window.history.pushState({}, document.title, '/');
          store.dispatch(storeUserLanguage(currentUserLanguage));
          store.dispatch(storeUser(user, isAuthorizedUser));
        }, 300);

        return user;
      }
    }

    return store.dispatch(storeUser(user, isAuthorizedUser));
  };

  const defineSigninMethod = () => {
    const receivedQueryStringObj = getURLQueryStringValues();
    const { culture } = receivedQueryStringObj;

    let selectedCulture = culture;
    const availableCultures = ['pt-BR', 'en-US'];
    const currentUserLanguage = localStorage.getItem('bi.userLanguage');

    // Troca idioma do sistema caso seja passado via querystring
    if (culture === null || culture === undefined || !availableCultures.includes(culture)) {
      selectedCulture = currentUserLanguage !== null && currentUserLanguage !== undefined
        ? currentUserLanguage : 'en-US';
    }

    localStorage.setItem('bi.userLanguage', selectedCulture);

    // Verifica se a URL tem serialNumber ou token para identificar o tipo de autenticação
    if (Object.prototype.hasOwnProperty.call(receivedQueryStringObj, 'serialNumber')) {
      return signinBySerialNumber(receivedQueryStringObj, selectedCulture);
    } if (Object.prototype.hasOwnProperty.call(receivedQueryStringObj, 'token')) {
      return signinByToken(receivedQueryStringObj, selectedCulture);
    }

    setTimeout(() => {
      window.history.pushState({}, document.title, '/');
      store.dispatch(storeUserLanguage(selectedCulture));
      store.dispatch(storeUser(user, isAuthorizedUser));
    }, 300);

    return user;
  };

  return {
    postAuthenticationBySerialNumber,
    getAuthenticationByToken,
    getURLQueryStringValues,
    signinBySerialNumber,
    signinByToken,
    defineSigninMethod,
  };
};

export default authService;
